import { ElementRef } from '@angular/core';

/** @docs-private */
export type Constructor<T> = new (...args: any[]) => T;

/**
 * This is a permissive type for abstract class constructors.
 * @docs-private
 */
export type AbstractConstructor<T = object> = abstract new (...args: any[]) => T;

/** @docs-private */
export interface CanIcon {
    /** Theme icon palette for the component. */
    icon: IconPalette;

    /** Default icon to fall back to if no value is set. */
    defaultIcon: IconPalette | undefined;
}

/** @docs-private */
export type CanIconCtor = Constructor<CanIcon>;

/** @docs-private */
export interface HasElementRef {
    _elementRef: ElementRef;
}

/** Possible icon palette values. */
export type IconPalette =
    | 'plus'
    | 'minus'
    | 'back'
    | 'round-back'
    | 'round-forth'
    | 'arrow-left'
    | 'arrow-right'
    | 'arrow-left-double'
    | 'arrow-right-double'
    | 'filter'
    | 'outlook'
    | 'gmail'
    | 'yahoo'
    | 'lighting-light-bulb'
    | 'edit-write'
    | 'logout'
    | undefined;

/** Mixin to augment a directive with a `icon` property. */
export function mixinIcon<T extends Constructor<HasElementRef>>(base: T, defaultIcon?: IconPalette): CanIconCtor & T {
    return class extends base {
        private _icon: IconPalette;
        defaultIcon = defaultIcon;

        get icon(): IconPalette {
            return this._icon;
        }
        set icon(value: IconPalette) {
            const iconPalette = value || this.defaultIcon;

            if (iconPalette !== this._icon) {
                if (this._icon) {
                    this._elementRef.nativeElement.classList.remove(`aumico-pattern-icon-${this._icon}`);
                }
                if (iconPalette) {
                    this._elementRef.nativeElement.classList.add(`aumico-pattern-icon-${iconPalette}`);
                }

                this._icon = iconPalette;
            }
        }

        constructor(...args: any[]) {
            super(...args);

            // Set the default icon that can be specified from the mixin.
            this.icon = defaultIcon;
        }
    };
}
